// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { sparseAssetSchema } from '@/asset/api/types';
import { FormSparseAssetSearch } from '@/asset/components/form-sparse-asset-search';
import { SupportSnackbar } from '@/components/support-snackbar';
import { Ticket } from '@/ticket/api/types';
import { useUpdateTicket } from '@/ticket/hooks/use-update-ticket';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

interface Props {
  isOpen: boolean;
  ticket: Ticket;
  onClose: () => void;
}

const formSchema = z.object({ assets: z.array(sparseAssetSchema) });

type FormData = z.infer<typeof formSchema>;

export function TicketAssetsDialog(props: Props) {
  const { control, formState, reset, handleSubmit, getFieldState } =
    useForm<FormData>({
      resolver: zodResolver(formSchema),
      defaultValues: { assets: props.ticket.assets },
    });

  const queryClient = useQueryClient();
  const updateTicketMutation = useUpdateTicket(props.ticket.key, {
    onSuccess: (data) => {
      queryClient.setQueryData(['tickets', props.ticket.key], data);
      reset({
        assets: data.assets,
      });
      props.onClose();
    },
  });

  const shouldSave = formState.isDirty;

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (shouldSave) {
      return updateTicketMutation.mutate({
        assets: data.assets.map((record) => record.id),
      });
    }
    props.onClose();
  };

  return (
    <>
      <SupportSnackbar
        isOpen={updateTicketMutation.isError}
        message="An error occurred while updating the project."
        onClose={updateTicketMutation.reset}
      />
      <Dialog
        fullWidth
        maxWidth="md"
        open={props.isOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') {
            return;
          }
          props.onClose();
        }}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(handleValidSubmission),
        }}
      >
        <DialogTitle>Attach assets</DialogTitle>
        <DialogContent dividers>
          <FormSparseAssetSearch
            multiple
            name="assets"
            label="Assets"
            control={control}
            helperText={getFieldState('assets').error?.message}
            disabled={updateTicketMutation.isPending}
          />
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            onClick={() => {
              reset({
                assets: props.ticket.assets,
              });
              props.onClose();
            }}
            disabled={updateTicketMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            size="small"
            type="submit"
            color="secondary"
            variant="contained"
            disabled={updateTicketMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
